import { useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'
import { useMutation, useQuery } from '@apollo/client'
import { Button, ButtonGroup, useToast } from '@chakra-ui/react'
import { graphql } from '@/__generated__/gql'
import { Drawer } from '@/components/drawer'
import { LoadingSection } from '@/components/loading-section'
import { SupplierForm, FormValues } from './components/supplier-form'

export const supplierItemFragmentDocument = graphql(`
  fragment SupplierItem on Supplier {
    id
    publicRef
    rating
    name
    organizationNumber
    email
    phone
    comment
    employeesCount
    employeesYear
    revenuesYear
    revenuesAmount
    businessSector
    createdAt
    updatedAt
    user {
      id
    }
    address {
      streetAddress
      postalCode
      postalArea
    }
    postAddress {
      streetAddress
      postalCode
      postalArea
    }
    contacts {
      name
      position
      email
      phone
    }
    categories {
      id
    }
  }
`)

const editSupplierDocument = graphql(`
  query EditSupplier($id: ID!) {
    supplier(id: $id) {
      ...SupplierItem
    }
  }
`)

const updateSupplierDocument = graphql(`
  mutation UpdateSupplier($id: ID!, $input: SupplierInput!) {
    updateSupplier(id: $id, input: $input) {
      message
      supplier {
        ...SupplierItem
      }
    }
  }
`)

export function EditSupplierRoute() {
  const params = z.object({ supplierId: z.string() }).parse(useParams())
  const navigate = useNavigate()
  const toast = useToast()
  const onCancel = useCallback(
    () => navigate(`../${params.supplierId}`),
    [navigate, params]
  )

  const { data, loading, error } = useQuery(editSupplierDocument, {
    variables: { id: params.supplierId },
  })
  const [updateSupplier, { loading: isSubmitting }] = useMutation(
    updateSupplierDocument,
    {
      onCompleted(data) {
        toast({
          status: 'success',
          title: data.updateSupplier.message,
          isClosable: true,
        })
        onCancel()
      },
      onError(error) {
        toast({
          status: 'error',
          title: error.message,
          isClosable: true,
        })
      },
    }
  )

  const onSubmit = useCallback(
    async (input: FormValues) => {
      await updateSupplier({
        variables: {
          id: params.supplierId,
          input: {
            ...input,
            postAddress: input.postAddress.streetAddress
              ? input.postAddress
              : null,
          },
        },
      })
    },
    [updateSupplier, params]
  )

  if (error) throw error

  const supplier = data?.supplier

  return (
    <Drawer
      size="lg"
      title="Endre leverandør"
      onClose={() => navigate('..')}
      footer={
        <ButtonGroup>
          <Button onClick={onCancel}>Avbryt</Button>
          <Button
            form="supplier-form"
            type="submit"
            colorScheme="blue"
            isLoading={isSubmitting}
          >
            Lagre leverandør
          </Button>
        </ButtonGroup>
      }
    >
      {loading && <LoadingSection />}
      {!!supplier && (
        <SupplierForm
          onSubmit={onSubmit}
          defaultValues={{
            name: supplier.name,
            organizationNumber: supplier.organizationNumber,
            rating: supplier.rating,
            userId: supplier.user?.id,
            email: supplier.email ?? undefined,
            phone: supplier.phone ?? undefined,
            comment: supplier.comment ?? undefined,
            employeesCount: supplier.employeesCount ?? undefined,
            employeesYear: supplier.employeesYear ?? undefined,
            revenuesYear: supplier.revenuesYear ?? undefined,
            revenuesAmount: supplier.revenuesAmount ?? undefined,
            businessSector: supplier.businessSector ?? undefined,
            address: {
              streetAddress: supplier.address.streetAddress,
              postalCode: supplier.address.postalCode,
              postalArea: supplier.address.postalArea,
            },
            postAddress: supplier.postAddress
              ? {
                  streetAddress: supplier.postAddress.streetAddress,
                  postalCode: supplier.postAddress.postalCode,
                  postalArea: supplier.postAddress.postalArea,
                }
              : undefined,
            contacts: supplier.contacts.map((contact) => ({
              name: contact.name,
              position: contact.position ?? undefined,
              email: contact.email ?? undefined,
              phone: contact.phone ?? undefined,
            })),
            categories: supplier.categories.map((category) => ({
              id: category.id,
            })),
          }}
        />
      )}
    </Drawer>
  )
}
